// Audio utilities for base64 audio playback with HTML5 Audio element
import { isAudioPause } from './textSpacing';

const blobCache = new Map();

function getAudioBlobUrl(b64) {
  if (!b64 || b64.length < 80) return null;
  if (blobCache.has(b64)) return blobCache.get(b64);
  try {
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    let mime = 'audio/mpeg';  
    if (bytes.length >= 4) {
      const head = bytes.slice(0, 4);
      if (head[0] === 0x52 && head[1] === 0x49) mime = 'audio/wav';  
      else if (head[0] === 0xFF && (head[1] & 0xE0) === 0xE0) mime = 'audio/mpeg';  
      else if (head[0] === 0x4F && head[1] === 0x67) mime = 'audio/ogg';  
    }
    const blob = new Blob([bytes], { type: mime });
    const url = URL.createObjectURL(blob);
    blobCache.set(b64, url);
    return url;
  } catch (e) {
    console.warn('Audio blob error:', e);
    return null;
  }
}

export function playBase64Audio(b64, volume = 1.0, onEnd = null) {
  const url = getAudioBlobUrl(b64);
  if (!url) {
    if (onEnd) onEnd();
    return null;
  }
  const audio = new Audio(url);
  audio.volume = Math.max(0, Math.min(1, volume));
  if (onEnd) {
    audio.addEventListener('ended', onEnd, { once: true });
    audio.addEventListener('error', () => onEnd(), { once: true });
  }
  audio.play().catch(e => {
    console.warn('Audio play failed:', e);
    if (onEnd) onEnd();
  });
  return audio;
}

export function playBase64Sequence(items, options = {}) {
  const { volume = 1.0, gapMs = 40, onEnd = null, onCharStart = null } = options;
  if (!items || !items.length) {
    if (onEnd) onEnd();
    return null;
  }

  let currentAudio = null;
  let index = 0;
  let stopped = false;

  function playNext() {
    if (stopped) return;
    while (index < items.length) {
      const item = items[index++];
      if (!item || !item.b64) continue;
      if (onCharStart) onCharStart(item.char, index - 1);
      const audio = new Audio(getAudioBlobUrl(item.b64));
      if (!audio) continue;
      audio.volume = Math.max(0, Math.min(1, volume));
      currentAudio = audio;
      const done = () => setTimeout(playNext, gapMs);
      audio.addEventListener('ended', done, { once: true });
      audio.addEventListener('error', done, { once: true });
      audio.play().catch(done);
      return; 
    }
    if (onEnd) onEnd();
  }

  playNext();
  return {
    stop: () => {
      stopped = true;
      if (currentAudio) {
        try { currentAudio.pause(); } catch(e) {}
        currentAudio = null;
      }
      if (onEnd) onEnd();
    }
  };
}

export function getCharAudioB64(char, voice = 'male') {
  if (typeof window !== 'undefined' && window.__geezWavLookup) {
    const wavData = window.__geezWavLookup[char];
    if (wavData && wavData[voice]) return wavData[voice];
  }
  return null;
}

export function playCharAudio(char, voice = 'male', volume = 1.0, onEnd = null) {
  const b64 = getCharAudioB64(char, voice);
  return b64 ? playBase64Audio(b64, volume, onEnd) : (onEnd && onEnd(), null);
}

export function playTextAudio(text, voice = 'male', options = {}) {
  const items = [];
  for (const ch of text) {
    if (isAudioPause(ch)) continue;
    const b64 = getCharAudioB64(ch, voice);
    if (b64) items.push({ char: ch, b64 });
  }
  return playBase64Sequence(items, options);
}

export function registerMp3Lookup(data) {
  if (typeof window !== 'undefined') window.__geezWavLookup = data;
}

// Legacy compatibility stubs
export function playWavBuffers() { return null; }
export function getCharWavBuffer() { return null; }
export function getCachedWavBuffer() { return null; }
export function mixWavBuffers() { return null; }

// Compacted Synthesizer Engine
class AudioSynthesizer {
  constructor() {
    this.ctx = null;
    this.initialized = false;
  }

  init() {
    if (this.initialized) return;
    this.ctx = new (window.AudioContext || window.webkitAudioContext)();
    this.initialized = true;
  }

  speakChar(char, options = {}) {
    this.init();
    const { baseFreq = 440, duration = 0.3, volume = 0.5, pitch = 1, formant = null } = options;
    const now = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    const filter = this.ctx.createBiquadFilter();

    osc.frequency.setValueAtTime(baseFreq * pitch, now);

    const vibrato = this.ctx.createOscillator();
    const vibratoGain = this.ctx.createGain();
    vibrato.frequency.value = 5;
    vibratoGain.gain.value = 2;
    vibrato.connect(vibratoGain);
    vibratoGain.connect(osc.frequency);
    vibrato.start(now);
    vibrato.stop(now + duration);

    filter.type = formant ? 'bandpass' : 'lowpass';
    filter.frequency.setValueAtTime(formant || 3000, now);
    if (formant) filter.Q.setValueAtTime(2, now);

    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(volume, now + 0.02);
    gain.gain.setValueAtTime(volume, now + duration * 0.7);
    gain.gain.linearRampToValueAtTime(0, now + duration);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(this.ctx.destination);
    osc.start(now);
    osc.stop(now + duration);

    return { osc, gain, duration };
  }

  speakWord(word, options = {}) {
    this.init();
    const { baseFreq = 300, pitchContour = 'flat', speed = 1 } = options;
    const chars = [...word];
    const charDuration = 0.2 / speed;
    const pauseDuration = 0.05 / speed;

    chars.forEach((char, i) => {
      let pitch = 1;
      if (pitchContour === 'rising') pitch = 0.8 + (i / chars.length) * 0.4;
      else if (pitchContour === 'falling') pitch = 1.2 - (i / chars.length) * 0.4;

      const vowelInfo = this.getVowelFormant(char);
      this.speakChar(char, { baseFreq, duration: charDuration, pitch, formant: vowelInfo ? vowelInfo.formant : null });
    });
  }

  generateWaveform(text, options = {}) {
    const { sampleRate = 44100, duration = 2, baseFreq = 300 } = options;
    const samples = Math.floor(sampleRate * duration);
    const waveform = new Float32Array(samples);
    const chars = [...text];
    const charDuration = duration / Math.max(chars.length, 1);

    for (let i = 0; i < samples; i++) {
      const t = i / sampleRate;
      const char = chars[Math.floor(t / charDuration)] || ' ';
      const vowelInfo = this.getVowelFormant(char);
      const freq = vowelInfo ? vowelInfo.freq : baseFreq;
      waveform[i] = Math.sin(2 * Math.PI * freq * t) * Math.sin(((t % charDuration) / charDuration) * Math.PI) * 0.5;
    }
    return waveform;
  }

  getVowelFormant(char) {
    const formants = {
      'ሀ': { formant: 700, freq: 300 }, 'ሁ': { formant: 730, freq: 310 },
      'ሂ': { formant: 2700, freq: 400 }, 'ሃ': { formant: 730, freq: 310 },
      'ሄ': { formant: 1700, freq: 350 }, 'ህ': { formant: 1500, freq: 280 },
      'ሆ': { formant: 850, freq: 320 },
    };
    return formants[char] || null;
  }

  speakWithBrowser(text, options = {}) {
    if (!('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = options.lang || 'gez-ET';
    utterance.rate = options.rate || 0.8;
    utterance.pitch = options.pitch || 1;
    utterance.volume = options.volume || 1;
    // Try Ge'ez script voices (Amharic, Tigrinya, Tigre, Sebat Bet, etc.)
    const geezVoice = window.speechSynthesis.getVoices().find(v => 
      v.lang.startsWith('am') || v.lang.startsWith('ti') || v.lang.startsWith('gez')
    );
    if (geezVoice) utterance.voice = geezVoice;
    window.speechSynthesis.speak(utterance);
    return utterance;
  }

  generateSpectrogramData(text, options = {}) {
    const { timeBins = 64, freqBins = 32 } = options;
    const waveform = this.generateWaveform(text);
    const spectrogram = Array(freqBins).fill(null).map(() => Array(timeBins).fill(0));
    const hopSize = Math.floor(waveform.length / timeBins);
    const windowSize = Math.min(hopSize * 2, 1024);

    for (let t = 0; t < timeBins; t++) {
      const start = t * hopSize;
      if (start + windowSize > waveform.length) break;
      for (let f = 0; f < freqBins; f++) {
        let sum = 0;
        const freq = f / freqBins;
        for (let w = 0; w < windowSize; w++) {
          sum += Math.abs(waveform[start + w] * Math.sin(Math.PI * w / windowSize) * Math.sin(2 * Math.PI * freq * w / windowSize));
        }
        spectrogram[f][t] = Math.min(sum / windowSize, 1);
      }
    }
    return spectrogram;
  }
}

export default AudioSynthesizer;